import { BarcodeIcon, ChevronDown } from "lucide-react";
import { SelectTrigger, SelectValue } from "../select/select";
import * as SelectPrimitive from "@radix-ui/react-select";
import { cn } from "@/lib/utils";
import { Cross1Icon } from "@radix-ui/react-icons";
import { Button } from "../button/button";
import {
  StatusOption,
  StatusPopup,
  statusTypeConfig,
} from "../status-popup/status-popup";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

export type Item = {
  id: string;
  label: string;
  isPending?: boolean;
  statusId?: StatusOption["id"];
};

export interface ItemStatusProps {
  item: Item;
  statusOptions: StatusOption[];
  onStatusChange: (itemId: Item["id"], statusId?: StatusOption["id"]) => void;
  icon?: React.ReactNode;
}

export function ItemStatus({
  item: { id, label, statusId },
  statusOptions,
  onStatusChange,
  icon,
}: ItemStatusProps) {
  const [open, setOpen] = useState(false);

  const option = statusOptions.find((status) => status.id === statusId);

  const displayIcon = option
    ? statusTypeConfig[option.type].icon
    : icon || <BarcodeIcon />;

  const handleOpen = () => setOpen(!open);

  const handleStatusChange = useCallback(
    (newStatusId: StatusOption["id"]) => {
      onStatusChange(id, newStatusId);
    },
    [id, onStatusChange]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      onStatusChange(id, undefined);
    },
    [id, onStatusChange]
  );

  return (
    <button
      onClick={handleOpen}
      className={cn(
        "flex justify-between bg-white text-black gap-2 rounded-md p-3 border",
        option && statusTypeConfig[option.type].bgMutedClass
      )}
    >
      <div className="flex gap-2 items-center">
        <span className="flex items-center justify-center h-5 w-5">
          {displayIcon}
        </span>
        <p className={option && statusTypeConfig[option.type].txtClass}>
          {label}
        </p>
      </div>
      <div className="flex items-center gap-2 h-full">
        {statusId && (
          <Button
            variant="ghost"
            size="xs"
            onClick={handleClear}
            className="h-full"
          >
            <Cross1Icon />
          </Button>
        )}

        <StatusPopup
          options={statusOptions}
          value={statusId ?? ""}
          onValueChange={handleStatusChange}
          popupOptions={{ open, setOpen, direction: "bottom", align: "end" }}
          trigger={<Trigger statusId={statusId} option={option} />}
        />
      </div>
    </button>
  );
}

function Trigger({
  statusId,
  option,
}: {
  statusId: Item["statusId"];
  option?: StatusOption;
}) {
  const { t } = useTranslation();
  const triggerClass = cn(
    "flex gap-1 items-center shadow-none border-b-[1px] rounded-none p-0 outline-none",
    "data-[placeholder]:text-muted-foreground [&[data-state=open]>svg]:-rotate-180",
    statusId && "border-black",
    option && statusTypeConfig[option.type].bgMutedClass,
    "text-md"
  );

  return (
    <SelectTrigger className={triggerClass}>
      <SelectValue placeholder={t("completeOperation.select")}>
        <p className="text-ms">{option?.label}</p>
      </SelectValue>
      <SelectPrimitive.Icon asChild>
        <ChevronDown
          className={cn("h-5 w-5 transition-transform duration-200")}
        />
      </SelectPrimitive.Icon>
    </SelectTrigger>
  );
}
